import { useEffect, useState } from 'react'
import { apiGrowthFollowers } from './studioApi.js'
import { Sparkline } from './Sparkline.jsx'
import { InfoCard, SkeletonText } from './ui-kit/index.js'

// FollowerGrowthCard (tech-debt backlog 2026-06-13, P1 item 4) — the
// distribution bottleneck's gauge. The studio tracked per-post engagement
// but had no account-level follower history, so growth read as "0" even
// when real. The autonomy worker writes one user_follower_snapshots row
// per provider per day; this card renders the trend so the operator sees
// "followers: N (+Δ in 7d)" at a glance instead of checking X manually.
//
// Best-effort by design: fetch failure or an empty table renders a quiet
// empty state, never an error banner — a missing gauge must not shout
// louder than the metrics that drive publishing.

function deltaOverDays(snapshots, days) {
  if (!Array.isArray(snapshots) || snapshots.length < 2) return null
  const newest = snapshots[0]
  const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
  // snapshots arrive newest-first; find the oldest row inside the window.
  const baseline = [...snapshots]
    .reverse()
    .find((row) => row.capturedAt >= cutoff)
  if (!baseline || baseline.id === newest.id) return null
  return Number(newest.followersCount) - Number(baseline.followersCount)
}

export function FollowerGrowthCard({ provider = 'x' }) {
  const [snapshots, setSnapshots] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    async function load() {
      try {
        const data = await apiGrowthFollowers({ provider, limit: 30 })
        if (mounted) setSnapshots(data?.snapshots || [])
      } catch {
        if (mounted) setSnapshots([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [provider])

  if (loading) {
    return (
      <InfoCard title="Followers" caption={provider.toUpperCase()}>
        <SkeletonText lines={2} />
      </InfoCard>
    )
  }

  if (!snapshots || snapshots.length === 0) {
    return (
      <InfoCard title="Followers" caption={provider.toUpperCase()}>
        <div style={{ color: 'var(--studio-text-tertiary)', fontSize: 12 }}>
          No snapshots yet — the first lands within 24h of the worker running
          with growth tracking on.
        </div>
      </InfoCard>
    )
  }

  const current = snapshots[0]
  const delta = deltaOverDays(snapshots, 7)
  // Oldest → newest for a left-to-right trend line.
  const values = [...snapshots]
    .reverse()
    .map((row) => Number(row.followersCount))

  return (
    <InfoCard title="Followers" caption={provider.toUpperCase()}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--studio-text)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {Number(current.followersCount).toLocaleString()}
        </span>
        {delta !== null && (
          <span
            style={{
              fontSize: 12,
              color: 'var(--studio-text-tertiary)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {delta >= 0 ? '+' : ''}
            {delta} in 7d
          </span>
        )}
      </div>
      {values.length >= 2 && (
        <div style={{ marginTop: 8 }}>
          <Sparkline
            values={values}
            width={240}
            height={36}
            ariaLabel={`${provider} follower trend, last ${values.length} snapshots`}
          />
        </div>
      )}
    </InfoCard>
  )
}
